import { Locale } from 'date-fns'
import * as dateFnsLocales from 'date-fns/locale'

/**
 * Formats a datetime into a string based on the provided locale.
 *
 * @param date
 * @param locale
 * @param includeTime
 * @return Formatted datetime string
 */
export const formatDateTime = (date: Date | string | undefined | null, locale: string, includeTime = true) => {
  if (!date) {
    return '-'
  }
  const dateObj = typeof date === 'string' ? new Date(date) : date

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date'
  }

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }

  try {
    const datePart = dateObj.toLocaleDateString(locale, dateOptions)
    const timePart = includeTime ? dateObj.toLocaleTimeString(locale, timeOptions) : ''

    return includeTime ? `${datePart} ${timePart}` : datePart
  } catch (error) {
    console.warn(`Locale ${locale} not supported, using ISO format`, error)
    return includeTime ? dateObj.toISOString().replace('T', ' ').slice(0, 19) : dateObj.toISOString().slice(0, 10)
  }
}

/**
 * Formats a Date object into a string based on the provided locale, without time.
 *
 * @param date
 * @param locale
 * @return Formatted date string
 */
export const formatDate = (date: Date | string, locale: string) => formatDateTime(date, locale, false)

/**
 * Formats a Date object into a local input-friendly string: YYYY-MM-DDTHH:mm
 * This is suitable for storing local datetime without seconds/timezone.
 */
export const formatToLocalInput = (d: Date | undefined | null): string => {
  if (!d || isNaN(d.getTime())) {
    return ''
  }
  const pad = (n: number) => n.toString().padStart(2, '0')
  const yyyy = d.getFullYear()
  const mm = pad(d.getMonth() + 1)
  const dd = pad(d.getDate())
  const hh = pad(d.getHours())
  const mi = pad(d.getMinutes())
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
}

/**
 * Parses a local input string (YYYY-MM-DDTHH:mm) to Date.
 * Returns undefined for empty/invalid values.
 */
export const parseLocalInput = (value?: string | null): Date | undefined => {
  if (!value) {
    return undefined
  }
  const d = new Date(value)
  return isNaN(d.getTime()) ? undefined : d
}

/**
 * Resolves a locale string to a date-fns Locale object.
 * It tries the full locale (e.g., 'tr-TR'), then the language part (e.g., 'tr'), and falls back to enUS.
 *
 * @param locale - The locale string to resolve (e.g., 'en-US', 'tr-TR')
 * @return The corresponding date-fns Locale object
 */
export const resolveLocale = (locale: string): Locale => {
  // 'tr-TR' → try 'trTR', then 'tr', then fall back to enUS
  const camelCase = locale.replace(/-([a-z])/gi, (_, c: string) => c.toUpperCase())
  const lang = locale.split('-')[0]

  return (
    (dateFnsLocales as Record<string, Locale>)[camelCase] ??
    (dateFnsLocales as Record<string, Locale>)[lang] ??
    dateFnsLocales.enUS
  )
}
